import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { motion } from 'framer-motion';
import { KeyboardArrowDown as ArrowDownIcon, WorkOutline as WorkIcon } from '@mui/icons-material';
import adjectivesData from '../data/adjectives.json';
import HeroBackground from './HeroBackground';
import FloatingTechLogos from './FloatingTechLogos';

const HeroSection: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';

  // Typing animation state
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  const roles = adjectivesData.roles;

  // Typing animation effect
  useEffect(() => {
    const currentRole = roles[currentRoleIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const deletingSpeed = 50;
    const pauseTime = 1500;

    if (!isDeleting && currentText === currentRole) {
      // Pause at the end of typing
      setTimeout(() => setIsDeleting(true), pauseTime);
      return;
    }

    if (isDeleting && currentText === '') {
      // Move to next role after deleting
      setIsDeleting(false);
      setCurrentRoleIndex((prev) => (prev + 1) % roles.length);
      return;
    }

    const timer = setTimeout(() => {
      if (isDeleting) {
        setCurrentText(currentRole.substring(0, currentText.length - 1));
      } else {
        setCurrentText(currentRole.substring(0, currentText.length + 1));
      }
    }, isDeleting ? deletingSpeed : typingSpeed);

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, currentRoleIndex, roles]);

  // Cursor blink effect
  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(cursorTimer);
  }, []);

  const scrollToAbout = () => {
    const element = document.querySelector('#prologue');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const downloadResume = () => {
    const link = document.createElement('a');
    link.href = '/PriyakantCharokar.pdf';
    link.download = 'PriyakantCharokar.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Theme-aware gradients - black and white
  const mainGradient = isDark
    ? 'linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #000000 100%)'
    : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 50%, #ffffff 100%)';
  const overlayGradient = isDark
    ? 'radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.03) 0%, transparent 50%)'
    : 'radial-gradient(circle at 20% 80%, rgba(0, 0, 0, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(0, 0, 0, 0.05) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(0, 0, 0, 0.03) 0%, transparent 50%)';
  const patternOpacity = isDark ? 0.08 : 0.1;
  const titleGradient = isDark
    ? 'linear-gradient(135deg, #ffffff 0%, #cccccc 50%, #ffffff 100%)'
    : 'linear-gradient(135deg, #000000 0%, #333333 50%, #000000 100%)';

  return (
    <Box
      id="home"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: mainGradient,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: overlayGradient,
          zIndex: 1,
        },
      }}
    >
      <HeroBackground />
      <FloatingTechLogos />
      {/* Animated Background Pattern */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: patternOpacity,
          backgroundImage: isDark
            ? `
              radial-gradient(circle at 25% 25%, white 2px, transparent 2px),
              radial-gradient(circle at 75% 75%, white 2px, transparent 2px)
            `
            : `
              radial-gradient(circle at 25% 25%, rgba(0,0,0,0.1) 2px, transparent 2px),
              radial-gradient(circle at 75% 75%, rgba(0,0,0,0.1) 2px, transparent 2px)
            `,
          backgroundSize: '60px 60px',
          animation: 'float 20s ease-in-out infinite',
          zIndex: 2,
          '@keyframes float': {
            '0%, 100%': {
              transform: 'translateY(0px)',
            },
            '50%': {
              transform: 'translateY(-20px)',
            },
          },
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 3, pt: { xs: 10, sm: 8, md: 8 }, pb: { xs: 4, md: 8 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 3, md: 4 },
            alignItems: 'center',
          }}
        >
          {/* Profile Image - First on Mobile, Right on Desktop */}
          <Box
            sx={{
              width: { xs: '100%', md: '41.666667%' },
              order: { xs: 1, md: 2 },
              display: 'flex',
              justifyContent: { xs: 'center', md: 'flex-end' },
              alignItems: 'center',
              position: 'relative',
              mb: { xs: 2, md: 0 },
            }}
          >
              <motion.div
                initial={{ opacity: 0, scale: 0.8, x: 50 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                transition={{ duration: 1.2, delay: 0.6, ease: 'easeOut' }}
                style={{ position: 'relative' }}
              >
                {/* Decorative rings */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '140px', sm: '240px', md: '360px' },
                    height: { xs: '140px', sm: '240px', md: '360px' },
                    borderRadius: '50%',
                    border: `2px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
                    zIndex: 0,
                    animation: 'pulse 3s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': {
                        transform: 'translate(-50%, -50%) scale(1)',
                        opacity: 0.5,
                      },
                      '50%': {
                        transform: 'translate(-50%, -50%) scale(1.1)',
                        opacity: 0.3,
                      },
                    },
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '160px', sm: '260px', md: '380px' },
                    height: { xs: '160px', sm: '260px', md: '380px' },
                    borderRadius: '50%',
                    border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'}`,
                    zIndex: 0,
                    animation: 'pulse 3s ease-in-out infinite 1.5s',
                  }}
                />
                
                {/* Profile Image Container */}
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 1,
                    width: { xs: '120px', sm: '200px', md: '320px' },
                    height: { xs: '120px', sm: '200px', md: '320px' },
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: `4px solid ${isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'}`,
                    boxShadow: isDark
                      ? '0 20px 60px rgba(0, 0, 0, 0.5), 0 0 0 8px rgba(255, 255, 255, 0.05), inset 0 0 40px rgba(0, 0, 0, 0.3)'
                      : '0 20px 60px rgba(0, 0, 0, 0.3), 0 0 0 8px rgba(0, 0, 0, 0.05), inset 0 0 40px rgba(255, 255, 255, 0.5)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'scale(1.05)',
                      boxShadow: isDark
                        ? '0 30px 80px rgba(0, 0, 0, 0.6), 0 0 0 12px rgba(255, 255, 255, 0.08), inset 0 0 50px rgba(0, 0, 0, 0.4)'
                        : '0 30px 80px rgba(0, 0, 0, 0.4), 0 0 0 12px rgba(0, 0, 0, 0.08), inset 0 0 50px rgba(255, 255, 255, 0.6)',
                    },
                  }}
                >
                  <Box
                    component="img"
                    src="/profile.png"
                    alt="Priyakant Charokar"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center',
                      display: 'block',
                    }}
                  />
                </Box>
              </motion.div>
          </Box>

          {/* Left Column - Text Content */}
          <Box
            sx={{
              width: { xs: '100%', md: '58.333333%' },
              order: { xs: 2, md: 1 },
              textAlign: { xs: 'center', md: 'left' },
              color: isDark ? 'rgba(255,255,255,0.96)' : theme.palette.text.primary,
              position: 'relative',
            }}
          >
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
              >
                <Typography
                  variant={isMobile ? 'h3' : 'h1'}
                  component="h1"
                  className="hero-name-allura"
                  sx={{
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    fontWeight: 400,
                    textShadow: isDark ? '0 4px 20px #000' : '0 4px 20px rgba(0,0,0,0.3)',
                    background: titleGradient,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.02em',
                    lineHeight: 1.1,
                    fontFamily: 'Allura, cursive !important',
                    fontSize: '42px !important',
                    '@media (min-width: 480px) and (max-width: 600px)': {
                      fontSize: '48px !important',
                    },
                    '@media (min-width: 601px) and (max-width: 960px)': {
                      fontSize: '54px !important',
                    },
                    '@media (min-width: 961px) and (max-width: 1280px)': {
                      fontSize: '58px !important',
                    },
                    '@media (min-width: 1281px)': {
                      fontSize: '63px !important',
                    },
                  }}
                >
                  Priyakant Charokar
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
              >
                <Typography
                  variant={isMobile ? 'h5' : 'h4'}
                  component="h2"
                  sx={{
                    mb: { xs: 3, sm: 3.5, md: 4 },
                    fontWeight: 400,
                    textShadow: isDark ? '0 2px 10px #000' : '0 2px 10px rgba(0,0,0,0.2)',
                    letterSpacing: '-0.01em',
                    opacity: 0.95,
                    fontFamily: '"Roboto Mono", "Courier New", monospace',
                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.75rem' },
                lineHeight: { xs: 1.3, sm: 1.3, md: 1.3 },
                '@media (min-width:601px) and (max-width:960px)': {
                  fontSize: '1.5rem',
                  lineHeight: 1.35,
                },
                  }}
                >
                  I am{' '}
                  <Box
                    component="span"
                    sx={{
                      color: theme.palette.text.primary,
                      fontWeight: 700,
                      textShadow: isDark 
                        ? '0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.6), 0 0 30px rgba(255,255,255,0.4), 0 0 40px rgba(255,255,255,0.2)'
                        : '0 0 10px rgba(0,0,0,0.3), 0 0 20px rgba(0,0,0,0.2), 0 0 30px rgba(0,0,0,0.1)',
                      animation: showCursor ? 'glow 2s ease-in-out infinite alternate, blink 1s infinite' : 'glow 2s ease-in-out infinite alternate',
                      borderRight: showCursor ? (isDark ? '3px solid #00ff88' : '3px solid #000000') : '3px solid transparent',
                      '@keyframes glow': {
                        '0%': {
                          textShadow: isDark 
                            ? '0 0 10px rgba(255,255,255,0.8), 0 0 20px rgba(255,255,255,0.6), 0 0 30px rgba(255,255,255,0.4), 0 0 40px rgba(255,255,255,0.2)'
                            : '0 0 10px rgba(0,0,0,0.3), 0 0 20px rgba(0,0,0,0.2), 0 0 30px rgba(0,0,0,0.1)',
                        },
                        '100%': {
                          textShadow: isDark 
                            ? '0 0 20px rgba(255,255,255,1), 0 0 30px rgba(255,255,255,0.8), 0 0 40px rgba(255,255,255,0.6), 0 0 50px rgba(255,255,255,0.4), 0 0 60px rgba(255,255,255,0.2)'
                            : '0 0 20px rgba(0,0,0,0.4), 0 0 30px rgba(0,0,0,0.3), 0 0 40px rgba(0,0,0,0.2), 0 0 50px rgba(0,0,0,0.1)',
                        },
                      },
                      '@keyframes blink': {
                        '0%, 50%': { borderRightColor: isDark ? '#00ff88' : '#000000' },
                        '51%, 100%': { borderRightColor: 'transparent' },
                      },
                    }}
                  >
                    {currentText}
                  </Box>
                </Typography>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.8, ease: 'easeOut' }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: { xs: 2, sm: 2.5, md: 3 },
                    maxWidth: { xs: '100%', md: 600 },
                    mx: { xs: 'auto', md: 0 },
                    textShadow: isDark ? '0 2px 8px #000' : '0 2px 8px rgba(0,0,0,0.2)',
                    opacity: 0.9,
                    fontWeight: 400,
                    fontFamily: '"Roboto Mono", "Courier New", monospace',
                fontSize: { xs: '0.875rem', sm: '0.9375rem', md: '1.0625rem' },
                lineHeight: { xs: 1.6, sm: 1.7, md: 1.8 },
                letterSpacing: '0.009em',
                '@media (min-width:601px) and (max-width:960px)': {
                  fontSize: '1rem',
                  lineHeight: 1.7,
                },
                  }}
                >
                  Technology Leader & Digital Transformation Expert
                </Typography>
              </motion.div>

              {/* Availability Highlight */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 1.0, ease: 'easeOut' }}
              >
                <Box
                  sx={{
                    mb: { xs: 2.5, sm: 4, md: 5 },
                    maxWidth: { xs: '100%', md: 650 },
                    mx: { xs: 'auto', md: 0 },
                    p: { xs: 1.5, sm: 2.5, md: 3 },
                    borderRadius: { xs: '8px', md: '12px' },
                    background: isDark 
                      ? 'linear-gradient(135deg, rgba(0, 200, 83, 0.08) 0%, rgba(0, 230, 118, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(0, 200, 83, 0.06) 0%, rgba(0, 230, 118, 0.03) 100%)',
                    border: isDark 
                      ? '1px solid rgba(0, 230, 118, 0.3)' 
                      : '1px solid rgba(0, 200, 83, 0.25)',
                    backdropFilter: 'blur(10px)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '4px',
                      height: '100%',
                      background: 'linear-gradient(180deg, #00C853, #00E676, #69F0AE)',
                      borderRadius: '4px 0 0 4px',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, md: 1 }, mb: { xs: 0.75, md: 1 } }}>
                    <Box
                      sx={{
                        width: { xs: 8, md: 10 },
                        height: { xs: 8, md: 10 },
                        borderRadius: '50%',
                        background: '#00E676',
                        boxShadow: '0 0 10px #00E676, 0 0 20px rgba(0, 230, 118, 0.5)',
                        animation: 'availablePulse 2s ease-in-out infinite',
                        '@keyframes availablePulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.7, transform: 'scale(1.2)' },
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: '"Roboto Mono", "Courier New", monospace',
                        fontWeight: 700,
                        fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                        color: '#00E676',
                        textTransform: 'uppercase',
                        letterSpacing: { xs: '0.05em', md: '0.1em' },
                      }}
                    >
                      Immediately Available To Join
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: '"Roboto Mono", "Courier New", monospace',
                      fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.85rem' },
                      lineHeight: { xs: 1.6, md: 1.8 },
                      color: isDark ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.75)',
                      '& strong': {
                        color: isDark ? '#ffffff' : '#000000',
                        fontWeight: 600,
                      },
                      '& a': {
                        color: '#00E676',
                        textDecoration: 'none',
                        fontWeight: 600,
                        borderBottom: '1px dashed #00E676',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          color: '#69F0AE',
                          borderBottomStyle: 'solid',
                        },
                      },
                    }}
                  >
                    Since <strong>September 2025</strong> — my last month at my previous organization — I've been on an exciting journey of growth and creation. 
                    I've collaborated with <strong>innovation centers and startups</strong>, driving technology-led initiatives that push boundaries. 
                    Along the way, I sharpened my expertise in <strong>Enterprise Risk Management</strong>, earning an international certification. 
                    But the real highlight? Diving deep into <strong>AI/ML and GenAI</strong>—and channeling that passion into building{' '}
                    <a href="https://www.yogyta.ai" target="_blank" rel="noopener noreferrer">Yogyta AI </a>
                     - a free tool empowering job seekers to craft winning resumes.
                  </Typography>
                </Box>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 1.4, ease: 'easeOut' }}
              >
                <Box sx={{ display: 'flex', gap: 3, justifyContent: { xs: 'center', md: 'flex-start' }, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={downloadResume}
                    startIcon={<WorkIcon sx={{ fontSize: '1.1rem' }} />}
                    sx={{
                      fontFamily: '"Roboto Mono", "Courier New", monospace',
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', md: '1rem' },
                      py: { xs: 1.4, md: 1.6 },
                      px: { xs: 3.5, md: 4.5 },
                      borderRadius: '8px',
                      color: isDark ? '#ffffff' : '#1a1a1a',
                      background: isDark 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      backdropFilter: 'blur(10px)',
                      border: isDark 
                        ? '1px solid rgba(255, 255, 255, 0.2)' 
                        : '1px solid rgba(0, 0, 0, 0.15)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      position: 'relative',
                      overflow: 'visible',
                      animation: 'heartbeat 1.5s ease-in-out infinite',
                      boxShadow: isDark
                        ? '0 0 20px rgba(255, 255, 255, 0.15), 0 0 40px rgba(255, 255, 255, 0.1), 0 0 60px rgba(255, 255, 255, 0.05)'
                        : '0 0 20px rgba(0, 0, 0, 0.08), 0 0 40px rgba(0, 0, 0, 0.05)',
                      '@keyframes heartbeat': {
                        '0%': {
                          transform: 'scale(1)',
                          boxShadow: isDark
                            ? '0 0 20px rgba(255, 255, 255, 0.15), 0 0 40px rgba(255, 255, 255, 0.1)'
                            : '0 0 20px rgba(0, 0, 0, 0.08), 0 0 40px rgba(0, 0, 0, 0.05)',
                        },
                        '14%': {
                          transform: 'scale(1.05)',
                          boxShadow: isDark
                            ? '0 0 30px rgba(255, 255, 255, 0.25), 0 0 60px rgba(255, 255, 255, 0.15), 0 0 80px rgba(255, 255, 255, 0.1)'
                            : '0 0 30px rgba(0, 0, 0, 0.12), 0 0 60px rgba(0, 0, 0, 0.08)',
                        },
                        '28%': {
                          transform: 'scale(1)',
                          boxShadow: isDark
                            ? '0 0 20px rgba(255, 255, 255, 0.15), 0 0 40px rgba(255, 255, 255, 0.1)'
                            : '0 0 20px rgba(0, 0, 0, 0.08), 0 0 40px rgba(0, 0, 0, 0.05)',
                        },
                        '42%': {
                          transform: 'scale(1.03)',
                          boxShadow: isDark
                            ? '0 0 25px rgba(255, 255, 255, 0.2), 0 0 50px rgba(255, 255, 255, 0.12)'
                            : '0 0 25px rgba(0, 0, 0, 0.1), 0 0 50px rgba(0, 0, 0, 0.06)',
                        },
                        '70%': {
                          transform: 'scale(1)',
                          boxShadow: isDark
                            ? '0 0 20px rgba(255, 255, 255, 0.15), 0 0 40px rgba(255, 255, 255, 0.1)'
                            : '0 0 20px rgba(0, 0, 0, 0.08), 0 0 40px rgba(0, 0, 0, 0.05)',
                        },
                      },
                      '&:hover': {
                        transform: 'scale(1.08)',
                        background: isDark 
                          ? 'rgba(255, 255, 255, 0.1)' 
                          : 'rgba(0, 0, 0, 0.05)',
                        border: isDark 
                          ? '1px solid rgba(255, 255, 255, 0.4)' 
                          : '1px solid rgba(0, 0, 0, 0.3)',
                        boxShadow: isDark
                          ? '0 0 40px rgba(255, 255, 255, 0.3), 0 0 80px rgba(255, 255, 255, 0.2), 0 0 120px rgba(255, 255, 255, 0.1)'
                          : '0 0 40px rgba(0, 0, 0, 0.15), 0 0 80px rgba(0, 0, 0, 0.1)',
                        animationPlayState: 'paused',
                      },
                      '&:active': {
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    Hire Me
                  </Button>
                </Box>
              </motion.div>
          </Box>
        </Box>
      </Container>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5, delay: 1.8 }}
        style={{
          position: 'absolute',
          bottom: 40,
          left: '50%',
          transform: 'translateX(-50%)',
          cursor: 'pointer',
          zIndex: 3,
        }}
        onClick={scrollToAbout}
      >
        <motion.div
          animate={{ y: [0, 12, 0] }}
          transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.8)' : theme.palette.text.primary,
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                fontFamily: '"Roboto Mono", "Courier New", monospace',
              }}
            >
              Scroll Down
            </Typography>
            <ArrowDownIcon
              sx={{
                fontSize: 32,
                color: isDark ? 'rgba(255,255,255,0.9)' : theme.palette.text.primary,
                filter: isDark ? 'drop-shadow(0 2px 4px #000)' : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
              }}
            />
          </Box>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default HeroSection; 